import { zodResolver } from '@hookform/resolvers/zod';
import {
  useAddMembersToChannelMutation,
  useInfoPanelDetails,
  useUpdateChannelNameMutation,
} from 'gcs-common/clients/api/entities/channels/channel-api';
import { useGetErpCustomerUsersQuery } from 'gcs-common/clients/api/entities/connections/connections-api';
import ProfileImage, { ProfileImageSize } from 'gcs-common/components/InfoPanel/ListComponents/ProfileImage';
import LoadingIndicator from 'gcs-common/components/LoadingIndicator/LoadingIndicator';
import { toast } from 'gcs-common/design-system/hooks/use-toast';
import { Button, ButtonVariant } from 'gcs-common/design-system/ui/button';
import { Checkbox } from 'gcs-common/design-system/ui/checkbox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from 'gcs-common/design-system/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from 'gcs-common/design-system/ui/form';
import FormTextField from 'gcs-common/design-system/ui/form-text-field';
import { ScrollArea } from 'gcs-common/design-system/ui/scroll-area';
import { getChannelCraftsmanConnections } from 'gcs-common/slices/channel/channelSelectors';
import type { Response } from 'gcs-common/types/typeHelper';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { z } from 'zod';

interface RowProps {
  user: Response<'/admin/connections/{connection_id}/connection-members', 'get'>['data'][number];
  onClick: () => void;
  checked: boolean;
  userInChannel: boolean;
  isLoading: boolean;
}

const Row = (props: RowProps) => {
  const { user, onClick, checked, userInChannel, isLoading } = props;

  return (
    <div className="box-border flex w-full items-center gap-3 p-2">
      <div className="shrink-0">
        <ProfileImage imageUrl={user.imageUrl} size={ProfileImageSize.SMALL} isRounded={true} />
      </div>

      <div className="w-0 flex-1">
        <p className="m-0 truncate p-0">{user.friendlyName}</p>
        {userInChannel && <p className="m-0 p-0 text-grey-12 text-xs">Bereits Mitglied</p>}
      </div>

      <div className="shrink-0">
        <Checkbox
          disabled={userInChannel || isLoading}
          id={user.id}
          checked={checked}
          value={user.id}
          onClick={onClick}
        />
      </div>
    </div>
  );
};

interface AddMembersModal {
  isOpen: boolean;
  onRequestClose: () => void;
}

const AddMembersModal = (props: AddMembersModal) => {
  const { isOpen, onRequestClose } = props;
  const { routeChannelId: selectedChannelId = '' } = useParams<{ routeChannelId: string }>();
  const connectionId = useSelector(getChannelCraftsmanConnections(selectedChannelId))[0]?.id;
  const { data: channelData, isLoading: channelLoading } = useInfoPanelDetails(selectedChannelId);
  const channelName = channelData?.channelName;

  const { mutateAsync: addMembers, isPending: isAddMembersPending } = useAddMembersToChannelMutation();
  const { mutateAsync: updateChannelName, isPending: isUpdateChannelNamePending } = useUpdateChannelNameMutation();

  const FormSchema = z
    .object({
      channelName: z.string().optional(),
      userIds: z.array(z.string()).min(1, { message: 'mindestens ein Benutzer ist erforderlich' }),
    })
    .superRefine((data, ctx) => {
      if (!channelName && !data.channelName?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['channelName'],
          message: 'Erforderlich',
        });
      }
    });

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      channelName: '',
      userIds: [],
    },
  });

  const { data: usersData, isLoading: usersLoading } = useGetErpCustomerUsersQuery({
    path: {
      connection_id: connectionId,
    },
  });

  const getUserIsInChannel = (userId: string): boolean => {
    if (!channelData) return false;
    return !!channelData.users.find((u) => u.id === userId);
  };

  const isLoading = !channelData || !usersData || channelLoading || usersLoading;
  const isSubmitting = isAddMembersPending || isUpdateChannelNamePending;

  const onSubmit = async (data: z.infer<typeof FormSchema>) => {
    if (!channelName && !data.channelName) {
      toast({
        description: 'Chatname erforderlich',
        duration: 5000,
        variant: 'destructive',
      });
      return;
    }

    let updateChannelNameRes: Awaited<ReturnType<typeof updateChannelName>> | undefined;
    if (!channelName && data.channelName) {
      updateChannelNameRes = await updateChannelName({
        path: {
          channel_id: selectedChannelId,
        },
        body: {
          channel_name: data.channelName,
        },
      });
    }

    if (updateChannelNameRes?.error) {
      toast({
        description: 'Fehler beim Aktualisieren des Chatnamens',
        duration: 5000,
        variant: 'destructive',
      });
      return;
    }

    addMembers({
      path: {
        channel_id: selectedChannelId,
      },
      body: {
        user_ids: data.userIds,
      },
    }).then(() => {
      toast({
        description: 'Nutzer erfolgreich hinzugefügt',
        duration: 5000,
        variant: 'success',
      });
      handleClose();
    });
  };

  const handleClose = () => {
    if (isSubmitting) return;
    form.reset();
    onRequestClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Mitglieder hinzufügen</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className="flex flex-col gap-10">
              {isLoading ? (
                <LoadingIndicator />
              ) : (
                <div className="flex flex-col gap-5">
                  {!channelName && (
                    <FormField
                      control={form.control}
                      name="channelName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Sie müssen dem Chat einen Namen geben, um Mitglieder hinzuzufügen</FormLabel>
                          <FormControl>
                            <FormTextField {...field} autoFocus placeholder="Channel Name" />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  )}
                  <ScrollArea className="h-72">
                    <FormField
                      control={form.control}
                      name="userIds"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <div className="[&>div:not(:last-child)]:border-0 [&>div:not(:last-child)]:border-neutral-6 [&>div:not(:last-child)]:border-b-thin [&>div:not(:last-child)]:border-solid">
                              {usersData.data.map((u) => {
                                const userIsInChannel = getUserIsInChannel(u.id);
                                return (
                                  <Row
                                    key={u.id}
                                    user={u}
                                    checked={userIsInChannel || field.value.includes(u.id)}
                                    onClick={() => {
                                      if (field.value.includes(u.id)) {
                                        field.onChange(field.value.filter((f) => f !== u.id));
                                      } else {
                                        field.onChange([...field.value, u.id]);
                                      }
                                    }}
                                    userInChannel={userIsInChannel}
                                    isLoading={isLoading || isSubmitting}
                                  />
                                );
                              })}
                            </div>
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </ScrollArea>
                  <FormMessage />
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant={ButtonVariant.soft_neutral} onClick={handleClose}>
                Abbrechen
              </Button>
              <Button
                variant={ButtonVariant.solid_accent}
                isLoading={isSubmitting}
                type="submit"
                disabled={isLoading || isSubmitting || !form.formState.isValid}
              >
                Hinzufügen
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default AddMembersModal;
